import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowSquareOut, MagnifyingGlass, Lightning } from '@phosphor-icons/react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '~/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog';
import { Button } from '~/components/ui/button';
import { Badge } from '~/components/ui/badge';
import { MarkdownRenderer } from '~/components/markdown-renderer';
import { Skeleton } from '~/components/ui/skeleton';
import { useSkillRunStatus } from '~/hooks/queries/use-inspector';
import { useResearch, useResearchCitations } from '~/hooks/queries/use-research';
import { useDocumentDetail } from '~/hooks/queries/use-documents';

interface SkillFailureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string;
  sessionId?: string;
  skillSlug?: string;
  /** Speaking headline (e.g. the plan title) shown instead of the raw slug —
   *  used for action-tasks so a failed card reads as its task, not
   *  "execute-plan". */
  title?: string;
  /** Fallback startedAt from the row (epoch ms) when status hasn't yet
   *  loaded a string startedAt. Lets the Started line render immediately. */
  startedAtFallback?: number;
  onDismiss: () => void;
}

// Render a timestamp in the viewer's own locale and timezone (browser
// defaults) — never a hardcoded de-DE / Europe/Berlin, which showed every
// tenant one dev's locale regardless of where they are (BUG-012).
function formatTimestamp(iso: string | number | undefined): string {
  if (iso === undefined) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function formatDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const seconds = Math.round(ms / 1000);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export function SkillFailureDialog({
  open,
  onOpenChange,
  runId,
  sessionId,
  skillSlug,
  title,
  startedAtFallback,
  onDismiss,
}: SkillFailureDialogProps) {
  const { data: status } = useSkillRunStatus(runId, open);
  const { data: run } = useResearch(open ? sessionId : undefined);
  const matchingRun = run && run.skillRunId === runId ? run : null;
  const { data: citations } = useResearchCitations(matchingRun?.id, {
    enabled: open && !!matchingRun,
  });

  const isDeepResearch =
    skillSlug === 'deep-research' || status?.skillSlug === 'deep-research';
  const TypeIcon = isDeepResearch ? MagnifyingGlass : Lightning;

  const startedIso = status?.startedAt;
  const completedIso = status?.completedAt;

  const startedDisplay = useMemo(() => {
    if (startedIso) return formatTimestamp(startedIso);
    if (typeof startedAtFallback === 'number') return formatTimestamp(startedAtFallback);
    return '';
  }, [startedIso, startedAtFallback]);

  const completedDisplay = useMemo(
    () => (completedIso ? formatTimestamp(completedIso) : ''),
    [completedIso],
  );

  const durationDisplay = useMemo(() => {
    const startMs = startedIso ? new Date(startedIso).getTime() : startedAtFallback;
    const endMs = completedIso ? new Date(completedIso).getTime() : null;
    if (!startMs || !endMs) return '';
    return formatDuration(endMs - startMs);
  }, [startedIso, startedAtFallback, completedIso]);

  const reason = status?.error || matchingRun?.errorMessage || '';
  const sourceCount = citations?.length ?? 0;
  const briefingFilename = matchingRun?.briefingDocFilename;
  const [briefingOpen, setBriefingOpen] = useState(false);
  const { data: briefingDoc, isLoading: briefingLoading } = useDocumentDetail(
    briefingFilename ?? null,
    { enabled: open && briefingOpen && !!briefingFilename },
  );

  function handleDismiss() {
    onDismiss();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* sm:max-w-3xl explicitly overrides shadcn DialogContent's default
       *  sm:max-w-sm — without the breakpoint prefix the desktop layout was
       *  locked to ~384px and unreadable. */}
      <DialogContent className="sm:max-w-3xl">
        {/* min-w-0 on the grid child + flex row so a long title truncates
         *  instead of blowing the modal width (see SkillResultDialog). */}
        <DialogHeader className="min-w-0">
          <DialogTitle className="flex items-center gap-2 min-w-0">
            <TypeIcon size={18} className="text-muted-foreground shrink-0" />
            {title ? (
              <span className="font-medium text-base truncate">{title}</span>
            ) : (
              <span className="font-mono text-base truncate">{status?.skillSlug || skillSlug || 'skill'}</span>
            )}
            <Badge variant="destructive" className="ml-1 shrink-0">Failed</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 text-sm">
          {matchingRun?.topic && (
            <section>
              <div className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                Topic
              </div>
              <div className="whitespace-pre-wrap font-mono text-sm text-foreground/90">
                {matchingRun.topic}
              </div>
            </section>
          )}

          {(startedDisplay || completedDisplay || durationDisplay) && (
            <section className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
              {startedDisplay && (
                <>
                  <span className="text-muted-foreground">Started</span>
                  <span className="tabular-nums">{startedDisplay}</span>
                </>
              )}
              {completedDisplay && (
                <>
                  <span className="text-muted-foreground">Completed</span>
                  <span className="tabular-nums">{completedDisplay}</span>
                </>
              )}
              {durationDisplay && (
                <>
                  <span className="text-muted-foreground">Duration</span>
                  <span className="tabular-nums">{durationDisplay}</span>
                </>
              )}
            </section>
          )}

          {matchingRun && (
            <section>
              <div className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-1.5">
                Sources
              </div>
              <div className="text-sm text-foreground/90">
                {sourceCount === 0 ? (
                  <>
                    <span className="font-mono">0</span> — the agent saved a
                    briefing without calling <span className="font-mono">research_cite</span>,
                    so the result is ungrounded. The watchdog demoted this run.
                  </>
                ) : (
                  <>
                    <span className="font-mono">{sourceCount}</span>{' '}
                    {sourceCount === 1 ? 'source' : 'sources'}
                  </>
                )}
              </div>
            </section>
          )}

          {briefingFilename && (
            <Accordion
              type="single"
              collapsible
              value={briefingOpen ? 'briefing' : ''}
              onValueChange={(v) => setBriefingOpen(v === 'briefing')}
              asChild
            >
              <section>
                <AccordionItem value="briefing" className="border-b-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <AccordionTrigger className="w-fit flex-none gap-1.5 py-0 text-xs uppercase tracking-wider text-muted-foreground/70 hover:text-foreground hover:no-underline">
                      Briefing
                    </AccordionTrigger>
                    <Link
                      to={`/files/doc/${encodeURIComponent(briefingFilename)}`}
                      onClick={() => onOpenChange(false)}
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ArrowSquareOut size={11} />
                      Open full page
                    </Link>
                  </div>
                  <div className="text-xs text-muted-foreground/70 font-mono mb-2 truncate">
                    {briefingFilename}
                  </div>
                  <AccordionContent className="pb-0">
                    <div className="rounded border border-border bg-card/40 p-3 max-h-96 overflow-auto">
                      {briefingLoading ? (
                        <Skeleton className="h-32 w-full" />
                      ) : briefingDoc ? (
                        <MarkdownRenderer content={briefingDoc.content} />
                      ) : (
                        <div className="text-sm text-muted-foreground italic">
                          Couldn't load the briefing. Open the full page for details.
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </section>
            </Accordion>
          )}

          <section>
            <div className="text-xs uppercase tracking-wider text-muted-foreground/70 mb-1.5">
              Failure reason
            </div>
            {reason ? (
              <pre className="font-mono whitespace-pre-wrap max-h-80 overflow-auto rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-foreground/90">
                {reason}
              </pre>
            ) : (
              <div className="text-sm text-muted-foreground italic">
                No reason recorded.
              </div>
            )}
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button variant="destructive" onClick={handleDismiss}>
            Dismiss
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
